import { yupResolver } from '@hookform/resolvers/yup';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useGetGroupsInSiteByUserAcQuery } from '../../apiRtk/accessControlApi';
import { useCheckDormakabaAccessForUserMutation, useGetSiteQuery } from '../../apiRtk/be4feApi';
import { FE_ROLES } from '../../config/consts';
import { useGetLoadedParams } from '../../hooks/paramsHooks';
import { ApplicationContext, IModalBase } from '../../modules/commonTypes';
import { actionsTS, selectors } from '../../modules/user';
import { TUserActionContext } from '../../modules/user/actionsTS';
import { IUser } from '../../modules/user/store';
import { isUserGuest } from '../../modules/user/utils';
import { ActionModal } from '../_DesignSystemWrappers/Modal/ActionModal';
import { CustomPinPanel } from '../CustomPinPanel';
import DormakabaModalPinAlert from '../DormakabaModalPinAlert';
import UserInfo from '../UserInfo';
import messages from './messages';
import { getValidationSchema } from './validationSchema';

interface IUserPinModalNew extends IModalBase {
  user: IUser;
  context: TUserActionContext;
}

export function UserPinModalNew({ context, onClose, open, user }: IUserPinModalNew) {
  const { companyId, siteId } = useGetLoadedParams(ApplicationContext.Site);
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { pinData } = {
    pinData: useSelector(selectors.getGeneratedUserPinData),
  };
  const { data: siteData, isFetching } = useGetSiteQuery({ companyId, siteId });
  const pinLengthSetting = siteData?.services?.ACCESS_CONTROL?.userPinLength;
  const [checkDormakabaAccess] = useCheckDormakabaAccessForUserMutation();
  const [hasDormakabaAccess, setHasDormakabaAccess] = useState(false);
  const [isCustomPinMode, setIsCustomPinMode] = useState(false);
  const shouldValidateLengthRef = useRef(false);
  const { data: userAccessGroups } = useGetGroupsInSiteByUserAcQuery({
    companyId: companyId.toString(),
    siteId: siteId.toString(),
    userId: user.id.toString(),
  });
  const isUserInNoAccessGroup = userAccessGroups?.length === 0;
  const displayDormakabaPinAlert =
    siteData?.integrations?.DORMAKABA?.enabled &&
    pinLengthSetting !== 6 &&
    (hasDormakabaAccess || isUserInNoAccessGroup);
  const hasError = useSelector(selectors.hasGeneratedUserPinError);

  const formMethods = useForm({
    defaultValues: { customPin: '' },
    mode: 'onChange',
    resolver: yupResolver(
      getValidationSchema(
        companyId.toString(),
        siteId.toString(),
        pinLengthSetting!,
        formatMessage,
        shouldValidateLengthRef
      )
    ),
  });

  async function onSave() {
    if (isCustomPinMode) {
      const isValid = await formMethods.trigger('customPin');
      if (!isValid) {
        return;
      }
      dispatch(actionsTS.createCustomPin(user.id, formMethods.getValues('customPin')!, pinData.id, context));
    } else {
      dispatch(actionsTS.saveUserPin(user.id, pinData.id, context));
    }
    onClose();
  }

  const setShouldValidateLength = (value: boolean) => {
    shouldValidateLengthRef.current = value;
    if (value) {
      formMethods.trigger('customPin');
    }
  };

  // eslint-disable-next-line react-hooks/incompatible-library
  const customPinValue = formMethods.watch('customPin') || '';
  const isCustomPinLengthValid = customPinValue.length === pinLengthSetting;
  const isButtonDisabled = hasError || (isCustomPinMode && (!isCustomPinLengthValid || !formMethods.formState.isValid));

  useEffect(() => {
    if (!open || !siteData?.integrations?.DORMAKABA?.enabled) {
      setHasDormakabaAccess(false);
      return;
    }
    checkDormakabaAccess({ companyId, siteId, userId: user.id })
      .unwrap()
      .then(() => setHasDormakabaAccess(true))
      .catch(() => setHasDormakabaAccess(false));
  }, [open, siteData?.integrations?.DORMAKABA?.enabled]);

  return (
    <ActionModal
      icon={<CheckIcon />}
      isPrimaryButtonDisabled={isButtonDisabled}
      muiDialogProps={{ maxWidth: 'xs' }}
      onClose={onClose}
      open={open}
      primaryButtonAction={onSave}
      primaryButtonText={
        user.role === FE_ROLES.LOGIN_LESS || isUserGuest(user.role)
          ? formatMessage(messages.userPinModalConfirmButtonLoginLess)
          : formatMessage(messages.userPinModalConfirmButton)
      }
      secondaryButtonText={formatMessage(messages.userPinModalCancelButton)}
      title={formatMessage(messages.userPinModalTitle)}
    >
      <Box sx={{ pt: 2 }}>
        <Typography variant="body2">
          <FormattedMessage {...messages.userPinModalAssigneeText} />
        </Typography>
      </Box>
      <Box sx={{ pt: 2 }}>
        <UserInfo user={user} />
      </Box>
      <FormProvider {...formMethods}>
        <Box sx={{ pt: 2 }}>
          <CustomPinPanel
            disableCopyButton={isButtonDisabled}
            isCustomPinMode={isCustomPinMode}
            setIsCustomPinMode={setIsCustomPinMode}
            setShouldValidateLength={setShouldValidateLength}
            showCustomPinButton={!(user.role === FE_ROLES.LOGIN_LESS || isUserGuest(user.role))}
            hasError={hasError}
            isFetching={isFetching}
            pin={pinData.pin || ''}
            pinLengthSetting={pinLengthSetting}
            onInit={() => {
              dispatch(actionsTS.generateUserPin(user.id));
            }}
            onGenerate={() => {
              dispatch(actionsTS.generateUserPin(user.id));
            }}
          />
        </Box>
      </FormProvider>
      <Box sx={{ pt: 2 }}>
        {user.role === FE_ROLES.LOGIN_LESS && (
          <Typography variant="body2">
            <FormattedMessage {...messages.userPinModalInfoText} />
          </Typography>
        )}
      </Box>
      {displayDormakabaPinAlert && <DormakabaModalPinAlert companyId={companyId} siteId={siteId} />}
    </ActionModal>
  );
}
